import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLang } from '../LangContext';
import { PROJECTS } from '../data';

function TypedLine({ text, speed = 22, onDone }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (n >= text.length) { onDone?.(); return; }
    const t = setTimeout(() => setN(n + 1), speed);
    return () => clearTimeout(t);
  }, [n, text, speed, onDone]);
  return <span>{text.slice(0, n)}</span>;
}

function colorizeJson(s) {
  return s
    .replace(/"([^"]+)"\s*:/g, '<span style="color:var(--fg)">"$1"</span>:')
    .replace(/:\s*"([^"]+)"/g, ': <span style="color:var(--accent-1)">"$1"</span>')
    .replace(/\[([^\]]+)\]/g, (m, inner) => '[<span style="color:var(--accent-2)">' + inner + '</span>]');
}

const dispatchAction = (type, value) =>
  window.dispatchEvent(new CustomEvent('portfolio:action', { detail: { type, value } }));

function Prompt() {
  return (
    <>
      <span className="term__prompt">lamine@portfolio</span>
      <span className="term__out">:</span>
      <span className="term__path">~</span>
      <span className="term__out">$ </span>
    </>
  );
}

const COMMANDS = [
  'help', 'whoami', 'projects', 'stack', 'contact', 'github', 'linkedin',
  'rain', 'theme', 'lang', 'clear', 'replay', 'sudo', 'hire-me',
  'ls', 'pwd', 'cat', 'echo', 'exit',
];

function HeroTerminal({ t }) {
  const { lang, toggleLang } = useLang();
  const term = t.hero.terminal;
  const SCRIPT = useMemo(() => [
    { kind: 'cmd',  text: term.whoamiCmd },
    { kind: 'out',  text: term.whoamiOut },
    { kind: 'cmd',  text: term.catCmd },
    { kind: 'json', lines: term.jsonLines },
    { kind: 'cmd',  text: term.stackCmd },
    { kind: 'out',  text: term.stackLines[0] },
    { kind: 'out',  text: term.stackLines[1] },
    { kind: 'out',  text: term.stackLines[2] },
    { kind: 'cmd',  text: term.pingCmd },
    { kind: 'ok',   text: term.pingOk },
    { kind: 'idle' },
  ], [term]);

  const [step, setStep] = useState(0);
  const [typedSub, setTypedSub] = useState(0);
  const [cleared, setCleared] = useState(false);
  const [hist, setHist] = useState([]);
  const [input, setInput] = useState('');
  const [cmdHist, setCmdHist] = useState([]);
  const [histIdx, setHistIdx] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const timersRef = useRef([]);

  const interactive = step >= SCRIPT.length - 1;

  const advance = () => setStep((s) => Math.min(s + 1, SCRIPT.length - 1));
  const later = (fn, ms) => { timersRef.current.push(setTimeout(fn, ms)); };
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollTop = endRef.current.scrollHeight;
  }, [step, typedSub, hist, input]);

  const add = (...lines) => setHist((h) => [...h, ...lines]);

  const doReplay = () => {
    timersRef.current.forEach(clearTimeout);
    setHist([]); setCleared(false); setStep(0); setTypedSub(0);
  };

  const run = (raw) => {
    const text = raw.trim();
    add({ kind: 'cmd', text: raw });
    if (!text) return;
    setCmdHist((h) => [...h, text]);

    const [cmd, ...args] = text.split(/\s+/);
    const arg = (args[0] || '').toLowerCase();

    switch (cmd.toLowerCase()) {
      case 'help':
        add({ kind: 'out', text: term.helpTitle });
        term.helpRows.forEach(([c, d]) => add({ kind: 'out', text: `  ${c.padEnd(19)} ${d}` }));
        break;
      case 'whoami':
        add({ kind: 'out', text: term.whoamiOut });
        break;
      case 'projects':
      case 'projets':
        add({ kind: 'out', text: term.projectsTitle });
        PROJECTS.forEach((p) => add({ kind: 'out', text: `  ${p.id} · ${p.name.padEnd(12)} ${p.live ? '● live' : ''}` }));
        add({ kind: 'ok', text: term.gotoOut('projects') });
        dispatchAction('goto', 'projects');
        break;
      case 'stack':
        term.stackLines.forEach((l) => add({ kind: 'out', text: l }));
        add({ kind: 'ok', text: term.gotoOut('stack') });
        dispatchAction('goto', 'stack');
        break;
      case 'contact':
        add({ kind: 'ok', text: term.gotoOut('contact') });
        dispatchAction('goto', 'contact');
        later(() => dispatchAction('contact', 'open'), 750);
        break;
      case 'github':
        add({ kind: 'ok', text: term.openOut('github.com/NovzChrono') });
        window.open('https://github.com/NovzChrono', '_blank', 'noopener');
        break;
      case 'linkedin':
        add({ kind: 'ok', text: term.openOut('linkedin.com/in/traore-lamine') });
        window.open('https://www.linkedin.com/in/traore-lamine-84bb7423a/', '_blank', 'noopener');
        break;
      case 'rain':
        if (arg === 'off') { dispatchAction('rain', 'off'); add({ kind: 'out', text: term.rainOff }); }
        else { dispatchAction('rain', 'on'); add({ kind: 'ok', text: term.rainOn }); }
        break;
      case 'theme':
        if (arg === 'dark' || arg === 'light') {
          dispatchAction('theme', arg);
          add({ kind: 'ok', text: term.themeOut(arg) });
        } else {
          add({ kind: 'out', text: 'usage: theme dark|light' });
        }
        break;
      case 'lang':
        if (arg === 'fr' || arg === 'en') {
          add({ kind: 'ok', text: term.langOut(arg) });
          if (arg !== lang) later(toggleLang, 250);
        } else {
          add({ kind: 'out', text: 'usage: lang fr|en' });
        }
        break;
      case 'sudo':
        if (args.join(' ').toLowerCase().includes('hire')) {
          term.sudoSteps.forEach((line, i) => later(() => add({ kind: i === 0 ? 'out' : 'ok', text: line }), 350 * (i + 1)));
          later(() => { dispatchAction('goto', 'contact'); }, 350 * term.sudoSteps.length + 400);
          later(() => { dispatchAction('contact', 'open'); }, 350 * term.sudoSteps.length + 1100);
        } else {
          add({ kind: 'err', text: term.sudoDenied });
        }
        break;
      case 'hire-me':
      case 'hireme':
        add({ kind: 'err', text: term.sudoDenied });
        break;
      case 'ls':
        add({ kind: 'out', text: term.lsOut });
        break;
      case 'pwd':
        add({ kind: 'out', text: term.pwdOut });
        break;
      case 'cat':
        if (arg.includes('now.json')) term.jsonLines.forEach((l) => add({ kind: 'json1', text: l }));
        else add({ kind: 'err', text: term.catErr(args[0] || '') });
        break;
      case 'echo':
        add({ kind: 'out', text: args.join(' ') });
        break;
      case 'exit':
        add({ kind: 'out', text: term.exitOut });
        break;
      case 'clear':
        setHist([]); setCleared(true);
        break;
      case 'replay':
        doReplay();
        break;
      default:
        add({ kind: 'err', text: term.notFound(cmd) });
    }
  };

  const complete = () => {
    const frag = input.trimStart().toLowerCase();
    if (!frag || frag.includes(' ')) return;
    const matches = COMMANDS.filter((c) => c.startsWith(frag));
    if (matches.length === 1) setInput(matches[0] + ' ');
    else if (matches.length > 1) {
      add({ kind: 'cmd', text: input }, { kind: 'out', text: matches.join('  ') });
    }
  };

  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      run(input); setInput(''); setHistIdx(null);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!cmdHist.length) return;
      const idx = histIdx === null ? cmdHist.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(idx); setInput(cmdHist[idx]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIdx === null) return;
      const idx = histIdx + 1;
      if (idx >= cmdHist.length) { setHistIdx(null); setInput(''); }
      else { setHistIdx(idx); setInput(cmdHist[idx]); }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      complete();
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setHist([]); setCleared(true);
    }
  };

  const onTerminalClick = () => {
    if (window.getSelection?.()?.toString()) return; // let people copy text
    if (!interactive) { setStep(SCRIPT.length - 1); return; } // skip intro
    inputRef.current?.focus({ preventScroll: true });
  };

  const renderLine = (line, key, isCurrent) => {
    if (line.kind === 'cmd') return (
      <div key={key}>
        <Prompt />
        {isCurrent ? <TypedLine text={line.text} onDone={advance} /> : <span className="term__cmd">{line.text}</span>}
      </div>
    );
    if (line.kind === 'out') return (
      <div key={key} className="term__out">
        {isCurrent ? <TypedLine text={line.text} speed={14} onDone={advance} /> : line.text}
      </div>
    );
    if (line.kind === 'ok') return (
      <div key={key} className="term__ok">
        {isCurrent ? <TypedLine text={line.text} speed={14} onDone={advance} /> : line.text}
      </div>
    );
    if (line.kind === 'err') return (
      <div key={key} className="term__err">{line.text}</div>
    );
    if (line.kind === 'json1') return (
      <div key={key} style={{ color: 'var(--fg-muted)' }} dangerouslySetInnerHTML={{ __html: colorizeJson(line.text) }} />
    );
    if (line.kind === 'json') return (
      <div key={key} style={{ color: 'var(--fg-muted)' }}>
        {line.lines.map((ln, i) => {
          if (!isCurrent) return <div key={i} dangerouslySetInnerHTML={{ __html: colorizeJson(ln) }} />;
          if (i < typedSub) return <div key={i} dangerouslySetInnerHTML={{ __html: colorizeJson(ln) }} />;
          if (i === typedSub) return (
            <div key={i}>
              <TypedLine text={ln} speed={10} onDone={() => {
                if (typedSub + 1 >= line.lines.length) advance();
                else setTypedSub(typedSub + 1);
              }} />
            </div>
          );
          return null;
        })}
      </div>
    );
    return null;
  };

  return (
    <div
      className="hero__terminal"
      onClick={onTerminalClick}
      title={interactive ? term.hint : term.skipHint}
    >
      <div className="hero__terminal-head">
        <div className="hero__terminal-dots">
          <span className="hero__terminal-dot" />
          <span className="hero__terminal-dot" />
          <span className="hero__terminal-dot" />
        </div>
        <span>~/lamine — zsh — 80×24</span>
      </div>
      <div className="hero__terminal-body" ref={endRef}>
        {!cleared && SCRIPT.slice(0, step + 1).map((line, i) => renderLine(line, i, i === step && !interactive))}
        {interactive && (
          <>
            {hist.length === 0 && !cleared && (
              <div className="term__hint">{term.tryHelp}</div>
            )}
            {hist.map((line, i) => renderLine(line, 'h' + i, false))}
            <div className="term__inputline">
              <Prompt />
              <span className="term__cmd">{input}</span>
              <span className="term__cursor" />
              <input
                ref={inputRef}
                className="term__input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                aria-label="Terminal interactif"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                enterKeyHint="go"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Matrix() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cols = 60, rows = 40;
    const grid = Array.from({ length: cols }, () => Array(rows).fill(' '));
    const speeds = Array.from({ length: cols }, () => 0.2 + Math.random() * 1.2);
    const positions = Array.from({ length: cols }, () => Math.random() * rows);
    const chars = '01';

    const stepGrid = () => {
      for (let c = 0; c < cols; c++) {
        positions[c] += speeds[c];
        if (positions[c] > rows) positions[c] = -10;
        for (let r = 0; r < rows; r++) {
          const dist = Math.abs(r - positions[c]);
          if (dist < 1) grid[c][r] = chars[Math.floor(Math.random() * 2)];
          else if (dist < 3 && Math.random() < 0.1) grid[c][r] = chars[Math.floor(Math.random() * 2)];
          else if (Math.random() < 0.005) grid[c][r] = ' ';
        }
      }
    };

    const render = () => {
      let out = '';
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) out += grid[c][r] + ' ';
        out += '\n';
      }
      el.textContent = out;
    };

    // Reduced motion: draw one static frame, no loop.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      for (let i = 0; i < 30; i++) stepGrid();
      render();
      return;
    }

    // ~12fps is plenty for character rain — a fraction of the CPU of 60fps.
    let raf = 0, last = 0, running = false;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - last < 80) return;
      last = now;
      stepGrid();
      render();
    };
    const startLoop = () => { if (!running) { running = true; raf = requestAnimationFrame(tick); } };
    const stopLoop  = () => { running = false; cancelAnimationFrame(raf); };

    // Only animate while the hero is actually on screen.
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) startLoop(); else stopLoop();
    });
    io.observe(el);

    return () => { stopLoop(); io.disconnect(); };
  }, []);
  return <pre className="matrix" ref={ref} aria-hidden="true" />;
}

function Stamp() {
  return (
    <div className="hero__stamp" aria-hidden="true">
      <svg viewBox="0 0 100 100">
        <defs>
          <path id="stamp-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
        </defs>
        <circle cx="50" cy="50" r="48" className="hero__stamp-ring" />
        <text className="hero__stamp-text">
          <textPath href="#stamp-circle" textLength="226" lengthAdjust="spacingAndGlyphs">
            OPEN FOR WORK · ABIDJAN · REMOTE · 5Y EXP ·
          </textPath>
        </text>
      </svg>
      <span className="hero__stamp-core">/</span>
    </div>
  );
}

export default function Hero({ liveRain, soundOn, toggleSound }) {
  const { lang, t } = useLang();
  const titles = t.hero.titles;
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
  }, [lang]);
  useEffect(() => {
    const timer = setInterval(() => setI((x) => (x + 1) % titles.length), 2200);
    return () => clearInterval(timer);
  }, [titles.length]);

  return (
    <section className="hero fullpage-section" id="hero">
      <div className="hero__grid-bg" />
      <div className="hero__glow" />
      <Matrix />

      <div className="hero__inner">
        <div>
          <div className="hero__meta">
            <span>{t.hero.meta1}</span>
            <span>{t.hero.meta2}</span>
          </div>
          <h1 className="hero__title">
            {t.hero.h1Pre}<br />
            <span className="accent">{t.hero.h1Accent}</span><br />
            <span className="slash">/</span>{titles[i]}.
          </h1>
          <p className="hero__sub" dangerouslySetInnerHTML={{ __html: t.hero.sub }} />
          {liveRain && (
            <button type="button" className="hero__weather" onClick={toggleSound}>
              <span className="hero__weather-dot" />
              <span>
                ☔ {t.hero.weather.live} — <u>{soundOn ? t.hero.weather.on : t.hero.weather.cta}</u>
              </span>
            </button>
          )}
        </div>
        <div className="hero__term-wrap">
          <HeroTerminal key={lang} t={t} />
          <Stamp />
        </div>
      </div>

      <div className="hero__ticker">
        <div className="hero__ticker-track">
          <span>NestJS</span><span className="sep">/</span>
          <span>Laravel</span><span className="sep">/</span>
          <span>Next.js</span><span className="sep">/</span>
          <span>Flutter</span><span className="sep">/</span>
          <span>React</span><span className="sep">/</span>
          <span>PostgreSQL</span><span className="sep">/</span>
          <span>MongoDB</span><span className="sep">/</span>
          <span>Redis</span><span className="sep">/</span>
          <span>Kafka</span><span className="sep">/</span>
          <span>TypeScript</span><span className="sep">/</span>
          <span>PHP</span><span className="sep">/</span>
          <span>Dart</span><span className="sep">/</span>
          <span>Next.js</span><span className="sep">/</span>
          <span>Laravel</span><span className="sep">/</span>
          <span>NestJS</span><span className="sep">/</span>
          <span>Flutter</span>
        </div>
      </div>
    </section>
  );
}
